import type { DealCard } from '@/app/components/search/utils/buildDealCards/dealCardTypes.ts';
import type { PROMOTION_TYPES } from '@/domainLogic/index.ts';
import useIsClient from '@/app/components/search/hooks/useIsClient.ts';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import getSearchParams from '@/app/components/search/common/getSearchParams.ts';
import { URLParameters } from '@/app/common/urlParameters.ts';
import React, { useContext } from 'react';
import { CardsContext } from '@/app/components/search/components/SearchPageContainer/ListContainer/CardsGrid/CardsContext.ts';
import { useSearchDealsPageData } from '@/app/components/search/searchDealsPageProvider.tsx';
import { useTranslation } from '@/app/components/translations/Translation.tsx';
import { getFeatures } from '@/app/components/search/components/EvolutionCard/Details/Features/getFeatures';
import FeaturesIterator from '@/app/components/search/components/EvolutionCard/Details/Features/FeaturesIterator';
import { FeaturesContainer } from '@/app/components/search/components/EvolutionCard/Details/Features/styled.ts';
import { useCommonClient } from '@/app/common/commonClientProvider.tsx';
import { SearchPageContext } from '@/app/components/search/components/SearchPageContainer/SearchPageContext.ts';
import { useSearchPageData } from '@/app/components/search/searchPageDataProvider.tsx';

type FeaturesProps = {
  dealCard?: DealCard;
  isOverlay: boolean;
  promotionTypes: PROMOTION_TYPES[];
};

const Features = ({ dealCard, promotionTypes, isOverlay }: FeaturesProps) => {
  const isClient = useIsClient();
  const { searchInfoData, searchInfoDetailData, localization, curiosityParams, searchParams, commonData } =
    usePageWrapperData();
  const { isDesktop } = useCommonClient();
  const { listView } = useContext(SearchPageContext);
  const useColumnLayout = isDesktop && !!listView;
  const { isWaiting, isLoading } = useContext(CardsContext);
  const { hasPreselectedFlightLink } = useSearchDealsPageData();
  const { productsData } = useSearchPageData();
  const { t } = useTranslation();
  const currency = localization?.currency;
  const soldOut = dealCard?.product?.dates?.soldOut;
  const transportData = dealCard?.transport?.data?.find(
    (d) => d.transportId === dealCard?.product?.transport?.transportId
  );

  // context searchParams can be stale for filters applied without a full search cycle
  const isMealFilterApplied = !!getSearchParams()?.[URLParameters.MEAL];

  const { features = [] } = getFeatures({
    dealCard,
    t,
    currency,
    commonData,
    localization,
    searchParams,
    isWaiting,
    isLoading,
    isOverlay,
    promotionTypes,
    curiosityParams,
    searchInfoData,
    searchInfoDetailData,
    hasPreselectedFlightLink,
    transportData,
    isClient,
    isMealFilterApplied,
    isApexResponse: !!productsData?.flags?.apex,
  });

  const showFeatures = features && features.length > 0;

  return showFeatures ? (
    <FeaturesContainer isOverlay={isOverlay} soldOut={soldOut} useColumnLayout={useColumnLayout}>
      <FeaturesIterator features={features} useColumnLayout={useColumnLayout} />
    </FeaturesContainer>
  ) : null;
};

export default Features;
